/**
 * Choosing a role in the static build.
 *
 * No password, because there is nothing to authenticate against. Choosing a
 * role here loads that role's recording out of the snapshot: what it could
 * reach, what it was refused, what it saw redacted. Nothing is being granted,
 * and the page says so rather than dressing a file read up as a sign-in.
 *
 * Two steps rather than the live tier's four, because the snapshot holds one
 * account per role and asking for an organisation and then an email would be
 * three clicks to reach a list of one.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, ApiError } from '../api/client'
import { snapshot } from '../api/static'
import { absolutePath } from '../app/paths'
import { Badge, Note, Spinner } from '../components/bits'

export function StaticAccess() {
  const { data: index, isLoading, error } = useQuery({
    queryKey: ['snapshot-index'], queryFn: snapshot.index, retry: 1,
  })
  const [category, setCategory] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [refused, setRefused] = useState<string | null>(null)

  const categories = useMemo(() => {
    if (!index) return []
    const seen = new Map<string, { code: string; name: string; name_ar: string | null; roles: number }>()
    for (const role of index.roles) {
      const entry = seen.get(role.category_code)
      if (entry) entry.roles += 1
      else seen.set(role.category_code, {
        code: role.category_code, name: role.category_name,
        name_ar: role.category_name_ar, roles: 1,
      })
    }
    return [...seen.values()]
  }, [index])

  const roles = useMemo(
    () => (index?.roles || []).filter((r) => r.category_code === category),
    [index, category])

  if (isLoading) {
    return <div className="signin"><Spinner what="the recording" /></div>
  }

  if (error || !index) {
    const problem = error as ApiError | undefined
    return (
      <div className="signin">
        <header className="signin-head">
          <span className="plate lg">FP</span>
          <div>
            <h1>Funding Plate</h1>
            <p>This build carries its own recording of the platform, and it
               could not be read.</p>
          </div>
        </header>
        <Note kind="bad">
          <p><b>{problem?.title || 'The snapshot is missing'}.</b>{' '}
            {problem?.detail}</p>
          <p>Two causes, and the first is much the commoner:</p>
          <pre className="formula">{`the page was opened as a file
  a browser refuses to fetch local files from a page, so serve the
  folder over HTTP instead:

    npx serve dist        or      python3 -m http.server -d dist 8000

the snapshot was never exported
  from the repository root, with the platform running:

    make static`}</pre>
        </Note>
      </div>
    )
  }

  const enter = async (roleCode: string) => {
    setBusy(roleCode)
    setRefused(null)
    try {
      const result = await api.signIn({
        email: '', password: '', role_code: roleCode })
      window.location.assign(absolutePath(result.landing))
    } catch (e) {
      setRefused((e as ApiError).detail || (e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const exported = new Date(index.generated_at)
  const when = Number.isNaN(exported.getTime())
    ? index.generated_at
    : exported.toISOString().slice(0, 10)

  return (
    <div className="signin">
      <header className="signin-head">
        <span className="plate lg">FP</span>
        <div>
          <h1>Funding Plate</h1>
          <p>A recording of the running platform, in a browser. Choose the role
             you want to look through.</p>
          <div className="scope-note">
            <Badge tone="warn">static build</Badge>
            <Badge tone="muted">{index.roles.length} roles</Badge>
            <Badge tone="muted">{index.pages} surfaces</Badge>
            <Badge tone="muted">{index.explainers} explainers</Badge>
            <Badge tone="muted">exported {when}</Badge>
          </div>
        </div>
      </header>

      <Note kind="warn">
        <p><b>Nothing here is enforced, and that is worth understanding before
          you show it to anybody.</b> On the platform, three tiers of
          authorisation run in the database on every request, the consent
          gateway decides every disclosure, and audit rows are written by
          triggers no code path can skip. This build has none of that behind it.
          It replays what the platform decided when the recording was taken.</p>
        <p className="xs muted">
          Which is why the refusals are real refusals and the redaction is real
          redaction: a role that was refused a surface is refused it here, and a
          column a role could not see is not in these files. What changed is who
          is deciding, which is nobody. Anything you do is held in this browser
          and goes nowhere.
        </p>
      </Note>

      <ol className="steps">
        <li className={category ? 'done' : 'now'}>
          <h2>1 · Which kind of participant</h2>
          <div className="pick">
            {categories.map((c) => (
              <button type="button" key={c.code}
                      className={`pick-card ${category === c.code ? 'on' : ''}`}
                      onClick={() => setCategory(c.code)}>
                <b>{c.name}</b>
                <span className="ar" dir="rtl">{c.name_ar}</span>
                <span className="xs muted">
                  {c.roles} role{c.roles === 1 ? '' : 's'} recorded
                </span>
              </button>
            ))}
          </div>
        </li>

        {category && (
          <li className="now">
            <h2>2 · Which role</h2>
            <div className="pick">
              {roles.map((r) => (
                <button type="button" key={r.role_code}
                        className="pick-card"
                        disabled={busy !== null}
                        onClick={() => void enter(r.role_code)}>
                  <b>{r.role_name}</b>
                  <span className="ar" dir="rtl">{r.role_name_ar}</span>
                  <small>{r.purpose}</small>
                  <span className="xs muted">
                    <code>{r.role_code}</code>
                    {r.partner_name ? ` · ${r.partner_name}` : ''}
                  </span>
                  <Badge tone="muted">
                    {r.surfaces} surface{r.surfaces === 1 ? '' : 's'}
                  </Badge>
                  {busy === r.role_code && <span className="xs">Opening…</span>}
                </button>
              ))}
            </div>
            <p className="xs muted">
              The surface count is what that role could actually reach when the
              recording was taken, refusals excluded. A role with few surfaces
              is a narrow role, not a gap in the build.
            </p>
          </li>
        )}
      </ol>

      {refused && <Note kind="bad"><b>Not opened.</b> {refused}</Note>}

      <footer className="signin-foot">
        <p className="xs muted">
          Recorded from {index.source} on {when}. To refresh it, run{' '}
          <code>make static</code> against a running platform. The same code
          builds against the live platform with <code>make web</code>, where
          every rule above is enforced again on every request.
        </p>
      </footer>
    </div>
  )
}
